import moment from 'moment';
import { Verb, trackUe, Noun } from '@trello/analytics';
import {
  hasAdvancedChecklistsBeta,
  hasAdvancedChecklists,
} from './has-advanced-checklists';

interface AdvancedCheckItem {
  name: string;
  validAssigneeUsernames: string[];
  orgProductNumber?: number;
}

const reAssignee = /^@([a-z0-9_]{3,})\b/;
const reDate = /\b\d\d\d\d-\d\d-\d\d$/;

// Returns true if there's something that looks like a mention at the front,
// even if it might not be a valid mention in this context
export function startsWithMention(name: string) {
  return reAssignee.test(name);
}

export function getAssigneeUsername(
  checkItem: AdvancedCheckItem,
): string | null {
  if (!hasAdvancedChecklists(checkItem.orgProductNumber)) {
    return null;
  }

  const initialMention = checkItem.name.match(reAssignee)?.[1];

  const assigneeUsername =
    initialMention && checkItem.validAssigneeUsernames.includes(initialMention)
      ? initialMention
      : null;

  return assigneeUsername;
}

// Returns true if there's something that looks like a date at the end of the
// name
export function endsWithDate(name: string) {
  return reDate.test(name);
}

export function getDueDate(checkItem: AdvancedCheckItem) {
  if (!hasAdvancedChecklists(checkItem.orgProductNumber)) {
    return null;
  }

  const potentialDueDate = checkItem.name.match(reDate)?.[0];

  const momentDate = potentialDueDate && moment(potentialDueDate);

  const due = momentDate && momentDate.isValid() ? momentDate.toDate() : null;

  return due;
}

export function trackNameChange(
  checkItem: AdvancedCheckItem,
  prevName: string,
  trackingMethod: string,
) {
  const prevAssigneeUsername = getAssigneeUsername({
    ...checkItem,
    name: prevName,
  });
  const prevDueDate = getDueDate({
    ...checkItem,
    name: prevName,
  });

  const newAssigneeUsername = getAssigneeUsername(checkItem);
  const newDueDate = getDueDate(checkItem);

  if (
    (prevAssigneeUsername || newAssigneeUsername) &&
    prevAssigneeUsername !== newAssigneeUsername
  ) {
    let verb = Verb.CHANGES;
    if (!prevAssigneeUsername && newAssigneeUsername) {
      verb = Verb.ADDS;
    } else if (prevAssigneeUsername && !newAssigneeUsername) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.MEMBER,
      'on checkitem',
      '',
      trackingMethod,
    );
  }

  if (
    (prevDueDate || newDueDate) &&
    prevDueDate?.getTime() !== newDueDate?.getTime()
  ) {
    let verb = Verb.CHANGES;
    if (!prevDueDate && newDueDate) {
      verb = Verb.ADDS;
    } else if (prevDueDate && !newDueDate) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      trackingMethod,
    );
  }
}

export function trackNameRender(checkItem: AdvancedCheckItem) {
  const prepObj = hasAdvancedChecklistsBeta(checkItem.orgProductNumber)
    ? 'on advanced checklists'
    : '';

  trackUe(
    Noun.CARD_DETAIL,
    Verb.RENDERS,
    Noun.CHECKLIST_ITEM,
    prepObj,
    '',
    'by viewing card detail',
  );

  if (getAssigneeUsername(checkItem)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.MEMBER,
      'on checkitem',
      '',
      'by having an mention at the beginning of checkitem text',
    );
  }

  if (getDueDate(checkItem)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      'by having a date-like string at the end of checkitem text',
    );
  }
}
